import { notifications } from '@mantine/notifications';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { getLogger } from 'loglevel';
import { useCallback } from 'react';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { DetailsLayout } from '@apple/ui/layouts/DetailsLayout';
import { ServerValidationError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { saveProductCategory } from '@apple/features/product/api/category';
import { CategoryForm } from '@apple/features/product/components/category-form';
import { categoryQueryKeys } from '@apple/features/product/queries/category';

const log = getLogger('manage-category');

export const Route = createFileRoute('/_authed/_admin/manage/categories_/add')({
	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.ProductCategoryManagement.Write',
		});
	},
	component: AddCategoryRoute,
});

function AddCategoryRoute() {
	const { t } = useTranslation('manage');
	const { queryClient } = Route.useRouteContext();

	const goBackToList = useBackOrNavigate();

	const onSuccessfulChange = useCallback(
		async (message: string) => {
			await queryClient.invalidateQueries({
				queryKey: categoryQueryKeys.list(),
			});
			notifications.show({
				message: message,
				icon: <Success />,
				color: 'green',
				autoClose: 5000,
			});
			await goBackToList('/manage/categories');
		},
		[goBackToList, queryClient],
	);

	const onFailedChange = useCallback(
		(error: Error) => {
			//TODO: Have the API return model validation errors
			if (!(error instanceof ServerValidationError)) {
				log.error(error);
				notifications.show({
					message: t('common:error.generic'),
					icon: <ErrorIcon />,
					color: 'red',
					autoClose: 5000,
				});
			}
		},
		[t],
	);

	const addCategoryMutation = useMutation({
		mutationFn: saveProductCategory,
		onSuccess: async () => {
			await onSuccessfulChange(t('categories.add.success'));
		},
		onError: onFailedChange,
	});

	return (
		<DetailsLayout title={t('categories.add.title')}>
			<CategoryForm onSave={addCategoryMutation.mutateAsync} />
		</DetailsLayout>
	);
}
